/**
 * Wire types shared with the API.
 *
 * They're hand-maintained rather than generated: the API is in this same
 * repo, so if you change a response shape in `apps/api`, change it here in the
 * same commit. If the surface grows past what's comfortable to keep in sync,
 * promote these to a `packages/contracts` workspace both sides import.
 */

import type {
  ClinicalFormAnswers,
  ClinicalFormDefinition,
} from "@dd/clinical-forms";
import type { PracticeSpecialty } from "./lib/practice-specialty.ts";

export type { PracticeSpecialty };

export type PrescriptionStatus =
  "draft" | "issuing" | "issued" | "failed" | "void_pending" | "voided";
export interface PrescriptionConnection {
  id: string;
  kind: "prescriber" | "payer";
  market: string;
  provider: string;
  mode: "sandbox" | "live";
  status: "active" | "disabled";
  scopes: string[];
}
export interface PrescriptionItem {
  medicationId: string;
  name: string;
  form: string | null;
  dose: string;
  route: string | null;
  frequency: string;
  indication: string | null;
  quantity: string | null;
  duration: string | null;
  instructions: string | null;
}
export interface Prescription {
  id: string;
  patientId: string;
  /** Null cuando la receta no nace de una consulta firmada. */
  encounterId: string | null;
  documentType: "electronic_prescription" | "professional_indication";
  status: PrescriptionStatus;
  market: string;
  provider: string;
  providerExternalId: string | null;
  items: PrescriptionItem[];
  patientSnapshot: { name: string; documentId: string };
  prescriberSnapshot: {
    name: string;
    credential: string | null;
    jurisdiction: string | null;
  };
  failureMessage: string | null;
  issuedAt: number | null;
  voidedAt: number | null;
  voidReason: string | null;
  createdAt: number;
}

/**
 * Marketing consent. Only `granted` may enter a campaign segment; `pending`
 * is someone still to ask, `revoked` someone to leave alone.
 */
export const MARKETING_CONSENTS = ["pending", "granted", "revoked"] as const;
export type MarketingConsent = (typeof MARKETING_CONSENTS)[number];

export interface Patient {
  id: string;
  ownerId: string;
  lifecycleState:
    "active" | "archived" | "deletion_requested" | "retained" | "purged";
  archivedAt: number | null;
  deletionRequestedAt: number | null;
  retainedUntil: number | null;
  purgedAt: number | null;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  documentId: string | null;
  /** ISO `YYYY-MM-DD`. */
  birthDate: string | null;
  notes: string | null;
  /** Historial médico, en campos separados para que se lea siempre. */
  conditions: string | null;
  allergies: string | null;
  medications: string | null;
  contraindications: string | null;
  source: string | null;
  /** La campaña por la que llegó, cuando llegó por una. */
  campaignId: string | null;
  marketingConsent: MarketingConsent;
  tags: string | null;
  /**
   * Seguimiento comercial, corregible a mano.
   *
   * `status` en null es "que lo decida la última visita": el sistema deduce
   * bien casi siempre, y un campo que hay que mantener a mano se desactualiza
   * solo. Se guarda un valor sólo cuando alguien contradice esa deducción.
   */
  status: "active" | "inactive" | null;
  /** Null es "sin registrar", que no es lo mismo que "no firmó". */
  consentSigned: boolean | null;
  nextTreatment: string | null;
  /** ISO `YYYY-MM-DD`. */
  nextTreatmentAt: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface TreatmentBudgetItem {
  id: string;
  treatmentId: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  discountBp: number;
  discountReason: string | null;
  taxBp: number;
  position: number;
}
export interface TreatmentBudgetVersion {
  id: string;
  version: number;
  currency: string;
  validUntil: number;
  terms: string | null;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
  sentAt: number | null;
  items: TreatmentBudgetItem[];
  acceptance: null | {
    decision: "accepted" | "partially_accepted" | "rejected";
    signerName: string;
    acceptedAt: number;
    items: Array<{ itemId: string; acceptedQuantity: number }>;
  };
}
export interface TreatmentBudget {
  id: string;
  patientId: string;
  status:
    | "draft"
    | "sent"
    | "accepted"
    | "partially_accepted"
    | "rejected"
    | "expired";
  currentVersion: number;
  updatedAt: number;
  versions: TreatmentBudgetVersion[];
  fulfillments: Array<{
    id: string;
    itemId: string;
    unitNumber: number;
    appointmentId: string | null;
    chargeId: string | null;
  }>;
}
export interface TreatmentBudgetDraft {
  patientId: string;
  currency: Currency;
  validUntil: number;
  terms: string | null;
  items: Array<{
    treatmentId: string | null;
    description: string;
    quantity: number;
    unitPrice: number;
    discountBp: number;
    discountReason: string | null;
    taxBp: number;
  }>;
}

export type ReferralStatus =
  | "draft"
  | "sent"
  | "received"
  | "contacted"
  | "scheduled"
  | "attended"
  | "report_pending"
  | "closed";
export interface ReferralContact {
  id: string;
  kind:
    | "internal_professional"
    | "internal_location"
    | "external_professional"
    | "institution";
  name: string;
  specialty: string | null;
  email: string | null;
  phone: string | null;
  professionalId: string | null;
  locationId: string | null;
  secureEmail: boolean;
  /** Los dados de baja no se ofrecen para derivaciones nuevas. */
  active: boolean;
}
export interface Referral {
  id: string;
  patientId: string;
  patientName: string | null;
  direction: "incoming" | "outgoing";
  originContactId: string | null;
  destinationContactId: string | null;
  origin: ReferralContact | null;
  destination: ReferralContact | null;
  specialty: string;
  reason: string;
  priority: "routine" | "priority" | "urgent";
  status: ReferralStatus;
  responsibleProfessionalId: string | null;
  sharedSections: Array<
    "clinical_summary" | "allergies" | "medications" | "problems" | "orders"
  >;
  clinicalSummary: string | null;
  accessExpiresAt: number | null;
  scheduledAt: number | null;
  attendedAt: number | null;
  closeOutcome: string | null;
  closeReason: string | null;
  updatedAt: number;
  attachments: Array<{ id: string; uploadId: string; label: string }>;
  events: Array<{
    id: string;
    kind: string;
    fromStatus: ReferralStatus | null;
    toStatus: ReferralStatus | null;
    note: string | null;
    actorName: string | null;
    occurredAt: number;
  }>;
  reports: Array<{
    id: string;
    version: number;
    body: string;
    signerName: string;
    createdAt: number;
  }>;
}
export interface ReferralMetrics {
  total: number;
  incoming: number;
  outgoing: number;
  converted: number;
  attended: number;
  closed: number;
  backlog: number;
  byOrigin: Array<{ id: string; count: number }>;
  byDestination: Array<{ id: string; count: number }>;
}

export interface ClinicalFormTemplate {
  id: string;
  ownerId: string;
  familyId: string;
  version: number;
  name: string;
  specialty: string | null;
  appointmentType: string | null;
  status: "draft" | "published" | "retired";
  definition: ClinicalFormDefinition;
  clonedFromPreset: string | null;
  publishedAt: number | null;
  retiredAt: number | null;
  /** Respuestas ya cargadas con esta versión; si hay alguna, no se elimina. */
  responseCount: number;
}
export interface ClinicalFormAssignment {
  id: string;
  templateFamilyId: string;
  scope: "organization" | "specialty" | "appointment_type";
  scopeValue: string | null;
}
export interface ClinicalFormResponse {
  id: string;
  patientId: string;
  templateVersionId: string;
  templateSnapshot: {
    templateId: string;
    familyId: string;
    version: number;
    name: string;
    specialty: string | null;
    appointmentType: string | null;
    definition: ClinicalFormDefinition;
  };
  answers: ClinicalFormAnswers;
  status: "draft" | "completed";
  authoredByName: string;
  updatedAt: number;
  completedAt: number | null;
}

export type InboxStatus =
  "new" | "assigned" | "waiting" | "snoozed" | "resolved";
export type InboxChannel =
  "whatsapp" | "email" | "microsite" | "portal" | "appointment_request";
export interface InboxContact {
  id: string;
  patientId: string | null;
  name: string;
  email: string | null;
  phone: string | null;
}
export interface InboxMessage {
  id: string;
  channel: InboxChannel;
  direction: "inbound" | "outbound" | "internal";
  body: string;
  deliveryStatus:
    | "received"
    | "ready_for_provider"
    | "queued"
    | "sent"
    | "delivered"
    | "read"
    | "failed";
  authorName: string | null;
  providerOccurredAt: number | null;
  createdAt: number;
}
export interface InboxConversation {
  id: string;
  contactId: string;
  contact: InboxContact;
  primaryChannel: InboxChannel;
  subject: string;
  status: InboxStatus;
  priority: "low" | "normal" | "high" | "urgent";
  assigneeUserId: string | null;
  assigneeName: string | null;
  team: string | null;
  tags: string[];
  locationId: string | null;
  campaignId: string | null;
  professionalId: string | null;
  appointmentId: string | null;
  clinical: boolean;
  slaDueAt: number | null;
  snoozedUntil: number | null;
  firstResponseAt: number | null;
  resolvedAt: number | null;
  convertedAt: number | null;
  updatedAt: number;
  messages: InboxMessage[];
  events: Array<{
    id: string;
    kind: string;
    detail: string | null;
    actorName: string | null;
    occurredAt: number;
  }>;
  suggestions: Array<{
    id: string;
    candidateContactId: string | null;
    candidatePatientId: string | null;
    confidence: number;
    reasons: string[];
  }>;
}
export interface InboxMetrics {
  backlog: number;
  resolved: number;
  converted: number;
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  slaBreaches: number;
}

export const APPOINTMENT_STATUSES = [
  "scheduled",
  "completed",
  "cancelled",
  "no_show",
] as const;

export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export const APPOINTMENT_CONSENTS = ["pending", "granted", "refused"] as const;
export type AppointmentConsent = (typeof APPOINTMENT_CONSENTS)[number];

export interface Appointment {
  id: string;
  ownerId: string;
  patientId: string;
  /** Epoch milliseconds. */
  startsAt: number;
  durationMin: number;
  status: AppointmentStatus;
  reason: string | null;
  treatmentId: string | null;
  additionalTreatments: Array<{
    treatmentId: string | null;
    name: string;
  }>;
  appointmentTypeId: string | null;
  /** Quién atiende, por referencia a su ficha en Profesionales. */
  professionalId: string | null;
  confirmation: "pending" | "confirmed";
  depositAmount: number;
  allDay: boolean;
  /**
   * El consentimiento del tratamiento, propio de esta cita: no se hereda de
   * una consulta anterior.
   */
  consent: AppointmentConsent;
  /** En qué sede se atiende. Null en clínicas de una sola sede. */
  locationId: string | null;
  modality: "onsite" | "remote";
  /** Epoch milliseconds. Null mientras el consentimiento siga pendiente. */
  consentAt: number | null;
  seriesId: string | null;
  seriesOccurrence: number | null;
  seriesDetachedAt: number | null;
  overbooked: boolean;
  overbookReason: string | null;
  overbookedById: string | null;
  overbookedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface AppointmentWithPatient extends Appointment {
  patientName: string;
  /**
   * El nombre resuelto desde la ficha, no una columna del turno: renombrar a
   * alguien en Profesionales cambia también su agenda pasada.
   */
  professional: string | null;
}

/** Alguien del equipo que atiende, administrado desde Organización. */
export interface Professional {
  id: string;
  ownerId: string;
  name: string;
  /** Especialidad o rol: "Dermatología", "Recepción". */
  role: string | null;
  email: string | null;
  status: "active" | "suspended" | "inactive";
  modality: "onsite" | "remote" | "both";
  defaultDurationMin: number;
  deactivatedAt: number | null;
  /** En qué sedes atiende. Vacío significa "en todas". */
  locationIds: string[];
  specialties: Array<{
    id: string;
    name: string;
    isPrimary: boolean;
    archivedAt: number | null;
  }>;
  createdAt: number;
  updatedAt: number;
}

export interface ProfessionalDetail extends Professional {
  accessAccount: { userId: string; role: OrganizationRole } | null;
}

export interface ProfessionalCredential {
  id: string;
  ownerId: string;
  professionalId: string;
  kind: string;
  number: string;
  jurisdiction: string;
  issuedAt: number | null;
  expiresAt: number | null;
  verificationStatus: "unverified" | "pending" | "verified" | "rejected";
  verifiedAt: number | null;
  verifiedBy: string | null;
  note: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface Specialty {
  id: string;
  ownerId: string;
  parentId: string | null;
  name: string;
  archivedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface ProfessionalActivity {
  id: string;
  patientId: string;
  patientName: string;
  startsAt: number;
  durationMin: number;
  status: AppointmentStatus;
  locationId: string | null;
  modality: "onsite" | "remote";
}

export interface ProfessionalMetrics {
  period: { from: number; to: number };
  appointments: {
    scheduled: number;
    attended: number;
    cancelled: number;
    noShow: number;
    uniquePatients: number;
    newPatients: number;
    recurringPatients: number;
    recurrenceRate: number | null;
  };
  capacity: {
    availableMinutes: number;
    bookedMinutes: number;
    attendedMinutes: number;
    freeMinutes: number;
    occupancyRate: number | null;
    utilizationRate: number | null;
  };
  duration: {
    averageMinutes: number | null;
    medianMinutes: number | null;
    validEncounters: number;
    attendedAppointments: number;
    coverageRate: number | null;
  };
  satisfaction: {
    averageScore: number | null;
    nps: number | null;
    responses: number;
    distribution: Record<string, number>;
  };
  finance: {
    collectedAmount: number;
    averageTicket: number | null;
    appointmentsWithPayment: number;
  };
  updatedAt: number;
}

export interface ProfessionalMetricsReport {
  professional: ProfessionalDetail;
  currency: string;
  current: ProfessionalMetrics;
  previous: ProfessionalMetrics;
  comparison: {
    appointments: { attended: Delta; uniquePatients: Delta; noShow: Delta };
    capacity: { occupancyRate: Delta | null; utilizationRate: Delta | null };
    satisfaction: { averageScore: Delta | null };
    finance: { collectedAmount: Delta };
  };
}

export interface ProfessionalCapacityReport {
  period: { from: number; to: number };
  currency: string;
  minimumSurveySample: number;
  updatedAt: number;
  rows: Array<{
    professional: Professional;
    current: ProfessionalMetrics;
    previous: ProfessionalMetrics;
    comparison: ProfessionalMetricsReport["comparison"];
    satisfactionComparable: boolean;
  }>;
}

export type PreconsultationAnswer =
  string | number | { unknown: true } | { uploadId: string };

export interface PreconsultationField {
  key: string;
  label: string;
  description?: string;
  type: "text" | "choice" | "date" | "number" | "scale" | "signature" | "file";
  required: boolean;
  allowUnknown?: boolean;
  options?: string[];
  min?: number;
  max?: number;
  clinicalMapping?:
    | {
        target: "allergy";
        category: "medication" | "food" | "environment" | "biologic" | "other";
        severity: "mild" | "moderate" | "severe";
      }
    | { target: "medication" };
}

export interface PreconsultationTemplate {
  id: string;
  ownerId: string;
  name: string;
  description: string | null;
  status: "active" | "retired";
  currentVersion: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface PreconsultationTemplateVersion {
  id: string;
  ownerId: string;
  templateId: string;
  version: number;
  fields: PreconsultationField[];
  createdBy: string;
  createdAt: number;
}

export interface PreconsultationSubmission {
  id: string;
  ownerId: string;
  templateVersionId: string;
  appointmentId: string;
  patientId: string;
  status: "sent" | "opened" | "completed" | "reviewed" | "expired";
  answers: Record<string, PreconsultationAnswer>;
  draftVersion: number;
  sentAt: number;
  openedAt: number | null;
  completedAt: number | null;
  reviewedAt: number | null;
  expiresAt: number;
  updatedAt: number;
}

export interface PreconsultationBundle {
  submission: PreconsultationSubmission;
  template: PreconsultationTemplate;
  version: PreconsultationTemplateVersion;
  reviews?: PreconsultationReview[];
}

export interface PreconsultationReview {
  id: string;
  submissionId: string;
  fieldKey: string;
  decision: "accepted" | "rejected" | "corrected";
  proposedValue: PreconsultationAnswer | null;
  finalValue: PreconsultationAnswer | null;
  reviewerName: string;
  createdAt: number;
}

/** Un servicio del catálogo de la clínica. */
export interface Treatment {
  id: string;
  ownerId: string;
  name: string;
  /** Facial inyectable, Aparatología, Piel, Corporal, Capilar, Íntima, Otros. */
  category: string;
  renewalIntervalMonths: number | null;
  /** Insumos que se proponen al registrar este tratamiento como realizado. */
  defaultSupplies: Array<{ itemId: string; quantity: number }>;
  createdAt: number;
  updatedAt: number;
}

export interface ScheduleResource {
  id: string;
  ownerId: string;
  locationId: string;
  name: string;
  type: "room" | "equipment" | "staff" | "other";
  status: "active" | "closed";
  capacity: number;
  interchangeableGroup: string | null;
  createdAt: number;
  updatedAt: number;
}

export const PHOTO_KINDS = ["before", "after"] as const;
export type PhotoKind = (typeof PHOTO_KINDS)[number];

/** Una foto clínica. El archivo se pide aparte, por una ruta con sesión. */
export interface PatientPhoto {
  id: string;
  ownerId: string;
  patientId: string;
  appointmentId: string | null;
  kind: PhotoKind;
  mimeType: string;
  byteSize: number;
  note: string | null;
  createdAt: number;
  deletedAt: number | null;
}

export const CONSENT_KINDS = [
  "treatment",
  "image",
  "data",
  "budget",
  "other",
] as const;
export type ConsentKind = (typeof CONSENT_KINDS)[number];

/** Un consentimiento firmado, con su documento en disco. */
export interface ConsentDocument {
  id: string;
  ownerId: string;
  patientId: string;
  appointmentId: string | null;
  kind: ConsentKind;
  title: string;
  mimeType: string;
  byteSize: number;
  /** Cuándo se firmó, que no siempre es cuándo se subió. */
  signedAt: number;
  signerContactId: string | null;
  signerName: string | null;
  signerCapacity: string | null;
  note: string | null;
  createdAt: number;
  deletedAt: number | null;
}

/**
 * Una nota del historial clínico, escrita a partir de una consulta o de la
 * ficha, con un archivo adjunto si hizo falta.
 */
export interface ConsultationNote {
  id: string;
  ownerId: string;
  patientId: string;
  /** Null si la nota es de la ficha, o si el turno del que salió se borró. */
  appointmentId: string | null;
  body: string;
  author: string | null;
  /**
   * El archivo adjunto. Las cuatro van juntas: o hay archivo o están las
   * cuatro en null. El contenido se pide aparte, por una ruta con sesión.
   */
  storageKey: string | null;
  mimeType: string | null;
  byteSize: number | null;
  fileName: string | null;
  /** Epoch milliseconds. */
  createdAt: number;
  updatedAt: number;
}

export interface ConsultationNoteWithAppointment extends ConsultationNote {
  /** Epoch milliseconds. Null cuando el turno ya no existe. */
  appointmentStartsAt: number | null;
  appointmentReason: string | null;
}

export type EncounterStatus =
  "draft" | "in_progress" | "signed" | "amended" | "entered_in_error";

export interface EncounterSnapshot {
  version: number;
  reason: string;
  anamnesis: string;
  examination: string;
  assessment: string;
  plan: string;
  authorId: string;
  authorName: string;
  signedById: string;
  signedByName: string;
  signedAt: number;
}

export interface EncounterAddendum {
  id: string;
  ownerId: string;
  encounterId: string;
  baseVersion: number;
  reason: string;
  content: string;
  authorId: string;
  authorName: string;
  createdAt: number;
}

export interface ClinicalEncounter {
  id: string;
  ownerId: string;
  idempotencyKey: string;
  patientId: string;
  appointmentId: string | null;
  professionalId: string | null;
  locationId: string | null;
  status: EncounterStatus;
  legacy: boolean;
  version: number;
  reason: string;
  anamnesis: string;
  examination: string;
  assessment: string;
  plan: string;
  authorId: string;
  authorName: string;
  startedAt: number | null;
  endedAt: number | null;
  signedAt: number | null;
  signedById: string | null;
  signedByName: string | null;
  signedSnapshot: EncounterSnapshot | null;
  errorReason: string | null;
  overrideReason: string | null;
  enteredInErrorAt: number | null;
  enteredInErrorById: string | null;
  createdAt: number;
  updatedAt: number;
  addenda?: EncounterAddendum[];
}

export interface AssistedEncounterProposal {
  draft: Pick<
    ClinicalEncounter,
    "reason" | "anamnesis" | "examination" | "assessment" | "plan"
  >;
  provider: "openai" | "local_extractive";
  model: string;
  transcriptHash: string;
  transcriptStored: false;
  requiresProfessionalReview: true;
}

export interface PlatformAssistantResponse {
  intent:
    "patients" | "appointments_today" | "finance_today" | "documents" | "help";
  answer: string;
  sources: string[];
  generatedAt: number;
}

/** La diferencia contra el período anterior, para el "▲ 12,4 % vs. junio". */
export interface Delta {
  change: number;
  /** Nulo cuando el período anterior fue cero: no se creció "infinito por ciento". */
  percent: number | null;
  direction: "up" | "down" | "flat";
}

export interface MonthlyMoney {
  /** Primer instante del mes, en epoch ms. */
  month: number;
  income: number;
  expenses: number;
  margin: number;
}

export interface DashboardSummary {
  patients: number;
  appointmentsToday: number;
  upcomingWeek: number;
  completionRate: number;
  /** Ausencias sobre turnos con desenlace. Distinta de la asistencia. */
  noShowRate: number;
  newPatientsThisMonth: number;
  /** Nulo si la clínica todavía no publicó disponibilidad. */
  occupancyRate: number | null;
  versusLastMonth: {
    income: Delta | null;
    newPatients: Delta;
    completionRate: Delta;
    noShowRate: Delta;
  };
  /** Seis meses consolidados en USD histórico. Nulo sin permiso o cobertura completa. */
  moneyByMonth: MonthlyMoney[] | null;
  next: AppointmentWithPatient[];
  /** Centavos USD históricos; nulo sin permiso o si falta alguna tasa. */
  incomeThisMonth: number | null;
  /** Centavos USD históricos; nulo sin permiso o si falta alguna tasa. */
  expensesThisMonth: number | null;
  /** Valores originales consolidados a USD con la cotización de cada día. */
  normalizedFinance: {
    baseCurrency: "USD";
    incomeThisMonth: number | null;
    versusLastMonthIncome: Delta | null;
    coverage: { normalizedEntries: number; totalEntries: number };
  } | null;
  criticalInventory: number;
  pendingConfirmation: number;
}

export type OrganizationRole =
  | "owner"
  | "admin"
  | "clinician"
  | "reception"
  | "billing"
  | "auditor"
  | "member";
export type ClinicalPermission =
  | "agenda.read"
  | "agenda.write"
  | "demographics.read"
  | "demographics.write"
  | "notes.read"
  | "notes.write"
  | "notes.error"
  | "documents.read"
  | "documents.write"
  | "finances.read"
  | "finances.write"
  | "team.read"
  | "team.write"
  | "metrics.read"
  | "settings.read"
  | "settings.write"
  | "audit.read"
  | "export.execute"
  | "retention.manage";

export interface RetentionPolicy {
  organizationId: string;
  region: string;
  clinicalRetentionDays: number;
  fiscalRetentionDays: number;
  backupRetentionDays: number;
  updatedBy: string;
  updatedAt: number;
}

export interface DeletionRequest {
  id: string;
  organizationId: string;
  patientId: string;
  status: "pending" | "approved" | "paused" | "completed" | "cancelled";
  reason: string;
  requestedBy: string;
  requestedAt: number;
  approvedBy: string | null;
  approvedAt: number | null;
  eligibleAt: number;
  legalHold: boolean;
  legalHoldReason: string | null;
  updatedAt: number;
}

export interface RetentionImpact {
  patient: {
    id: string;
    name: string;
    lifecycleState: Patient["lifecycleState"];
  };
  inventory: Array<{
    category: string;
    count: number;
    earliestAt: number | null;
    latestAt: number | null;
  }>;
  policy: RetentionPolicy;
  eligibleAt: number;
  backupExpiresAt: number;
  notes: string[];
  request: DeletionRequest | null;
}

export interface PurgeJob {
  id: string;
  status: "pending" | "running" | "completed" | "failed";
  report: {
    patientId: string;
    executedAt: number;
    deleted: Record<string, number>;
    retained: Record<string, number>;
    objectKeys: string[];
    objectDeletionFailures: string[];
    backupExpiresAt: number;
  } | null;
  error: string | null;
}
export interface SecurityPolicy {
  organizationId: string;
  sessionLifetimeHours: number;
  inactivityHours: number;
  stepUpMinutes: number;
  requireMfa: boolean;
  updatedAt: number;
}
export interface OrganizationInvitation {
  id: string;
  email: string;
  role: OrganizationRole;
  status: "pending" | "accepted" | "revoked";
  createdAt: number;
}
/** Una sede de la clínica. */
export interface Location {
  id: string;
  organizationId: string;
  name: string;
  address: string | null;
  phone: string | null;
  createdAt: number;
  updatedAt: number;
}

/** El semáforo de caja de una sede. */
export interface SedeCashState {
  status: "open" | "closed" | "none";
  sessionId: string | null;
  /** `contado − esperado` del último cierre. Nulo si todavía no cerró. */
  difference: number | null;
  openedAt: number | null;
}

/** Una fila del panel de sedes, con el mes en curso ya contado. */
export interface SedePanelRow {
  locationId: string;
  name: string;
  professionals: number;
  attended: number;
  collected: number;
  versusLastMonth: Delta;
  outstanding: number;
  cash: SedeCashState;
}

export interface SedePanel {
  from: number;
  to: number;
  currency: string;
  rows: SedePanelRow[];
  totals: {
    professionals: number;
    attended: number;
    collected: number;
    outstanding: number;
    /** Cajas abiertas tarde o cerradas con diferencia: lo que hay que mirar. */
    cashAlerts: number;
  };
}

export interface CashSession {
  id: string;
  locationId: string;
  shift: string | null;
  status: "open" | "closed";
  currency: string;
  openingFloat: number;
  openedBy: string;
  openedAt: number;
  countedTotal: number | null;
  expectedTotal: number | null;
  difference: number | null;
  closedBy: string | null;
  closedAt: number | null;
  notes: string | null;
}

export interface CashMovement {
  id: string;
  cashSessionId: string;
  kind: "expense" | "withdrawal" | "deposit" | "adjustment";
  amount: number;
  currency: string;
  description: string;
  reason: string | null;
  createdBy: string;
  occurredAt: number;
}

/** Lo que la caja debería tener adentro, partido por origen. */
export interface CashExpectation {
  openingFloat: number;
  collected: number;
  deposits: number;
  paidOut: number;
  expected: number;
}

export interface CashSessionDetail {
  session: CashSession;
  movements: CashMovement[];
  expectation: CashExpectation;
}

export interface PayoutScheme {
  id: string;
  professionalId: string;
  locationId: string | null;
  treatmentId: string | null;
  kind: "percentage" | "fixed" | "fixed_plus_percentage";
  /** Puntos básicos: 4550 son 45,5 %. */
  percentageBps: number | null;
  fixedAmount: number | null;
  currency: string;
  effectiveFrom: number;
  effectiveTo: number | null;
}

export interface ProfessionalPayout {
  id: string;
  professionalId: string;
  locationId: string;
  periodStart: number;
  periodEnd: number;
  currency: string;
  status: "draft" | "closed" | "paid";
  baseAmount: number;
  grossAmount: number;
  deductionsAmount: number;
  netAmount: number;
  notes: string | null;
  closedAt: number | null;
  paidAt: number | null;
}

export interface PayoutLine {
  id: string;
  payoutId: string;
  ledgerTransactionId: string | null;
  treatmentId: string | null;
  description: string;
  baseAmount: number;
  schemeKind: PayoutScheme["kind"];
  percentageBps: number | null;
  fixedAmount: number | null;
  amount: number;
  occurredAt: number;
}

export interface PayoutDeduction {
  id: string;
  payoutId: string;
  kind: "supplies" | "room_rent" | "card_fee" | "tax" | "other";
  description: string;
  amount: number;
}

export interface PayoutDetail {
  payout: ProfessionalPayout;
  lines: PayoutLine[];
  deductions: PayoutDeduction[];
}

export interface Organization {
  id: string;
  name: string;
  /** Lo que la clínica declaró al darse de alta. No es cuánta gente entra. */
  employeeCount: number;
  /** Cuántas personas tienen acceso: el largo de «Miembros del equipo». */
  memberCount: number;
  primaryColor: string;
  accentColor: string;
  /** La paleta del catálogo que se eligió. Null si nunca se eligió una. */
  paletteId: string | null;
  hasLogo: boolean;
  logoUpdatedAt: number | null;
  /**
   * La moneda local de la clínica: la que el interruptor de finanzas ofrece
   * junto al dólar, que está siempre.
   */
  currency: Currency;
  /**
   * A qué rama de la medicina se dedica la práctica.
   *
   * Null mientras nadie la haya elegido, que es el caso de toda clínica dada
   * de alta antes de que este campo existiera. No es lo mismo que `"other"`:
   * eso es una elección, esto es su ausencia.
   */
  practiceSpecialty: PracticeSpecialty | null;
  /** Horario de atención, en minutos desde medianoche: 540 = 9:00. */
  opensAt: number;
  closesAt: number;
  timezone: string;
  role: OrganizationRole;
  permissions: ClinicalPermission[];
  accessScope: {
    professionalId: string | null;
    locationIds: string[];
    patientScope: "all" | "assigned";
  };
  invitations: OrganizationInvitation[];
  createdAt: number;
  updatedAt: number;
  setupCompletedAt: number;
}
export interface OrganizationStatus {
  setupComplete: boolean;
  organization: Organization | null;
  organizations: Array<
    Pick<
      Organization,
      | "id"
      | "name"
      | "primaryColor"
      | "accentColor"
      | "role"
      | "permissions"
      | "hasLogo"
      | "logoUpdatedAt"
    >
  >;
}
export interface OrganizationMember {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: OrganizationRole;
  professionalId: string | null;
  locationIds: string[];
  patientScope: "all" | "assigned";
  createdAt: number;
}
export interface AuditEvent {
  id: string;
  organizationId: string;
  actorId: string;
  action: string;
  resource: string;
  resourceId: string | null;
  outcome: "success" | "denied" | "failure";
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: number;
}
export interface InventoryItem {
  id: string;
  name: string;
  category: string | null;
  stock: number;
  minimumStock: number;
  /** Unidad de aplicación en la que se expresa el stock (unidades, ml, etc.). */
  unit: string;
  presentation: string;
  presentationQuantity: number;
  openShelfLifeHours: number | null;
  presentationCost: number | null;
  presentationCostCurrency: Currency | null;
  /** En qué sede está ese stock. Null si se comparte entre todas. */
  locationId: string | null;
  updatedAt: number;
}
export interface InventoryLot {
  id: string;
  ownerId: string;
  itemId: string;
  locationId: string | null;
  lotNumber: string;
  reference: string | null;
  supplier: string | null;
  expiresAt: number | null;
  openedAt: number | null;
  openExpiresAt: number | null;
  recalledAt: number | null;
  recallReason: string | null;
  createdAt: number;
  updatedAt: number;
}
export interface InventoryLotSummary {
  lot: InventoryLot;
  itemName: string;
  baseUnit: string;
  stock: number;
  alert: "recalled" | "expired" | "expiring" | null;
}
export interface InventoryLotMovement {
  id: string;
  ownerId: string;
  itemId: string;
  lotId: string;
  locationId: string | null;
  type:
    | "receipt"
    | "consumption"
    | "adjustment"
    | "transfer_out"
    | "transfer_in"
    | "discard"
    | "return"
    | "migration";
  quantityDelta: number;
  enteredQuantity: number;
  enteredUnit: string;
  conversionFactor: number;
  transferId: string | null;
  performedTreatmentId: string | null;
  encounterId: string | null;
  reason: string | null;
  expiredOverrideById: string | null;
  expiredOverrideReason: string | null;
  occurredAt: number;
  actorId: string;
  actorName: string;
  createdAt: number;
}
/**
 * Las monedas en que se cobra. Sin conversión: cada una se suma por su lado.
 * Las locales de los países donde hay clínicas, y el dólar, que está siempre.
 */
export const CURRENCIES = [
  "ARS",
  "BOB",
  "BRL",
  "CLP",
  "COP",
  "CRC",
  "DOP",
  "GTQ",
  "HNL",
  "MXN",
  "NIO",
  "PAB",
  "PEN",
  "PYG",
  "UYU",
  "USD",
] as const;
export type Currency = (typeof CURRENCIES)[number];

/** Una campaña publicitaria con lo que volvió de ella. */
export interface CampaignReturn {
  id: string;
  ownerId: string;
  name: string;
  channel: string;
  /** Inversión en centavos. */
  spend: number;
  /** En qué moneda se pagó la inversión. */
  spendCurrency: Currency;
  spentAt: number | null;
  /** Consultas que generó: mensajes, llamados, formularios. */
  enquiries: number;
  message: string | null;
  emailSubject: string | null;
  channels: Array<"email" | "whatsapp"> | null;
  segment: {
    tags: string[];
    treatmentId: string | null;
    monthsSinceLastVisit: number | null;
    birthdayMonth: number | null;
  } | null;
  promotionalPrice: number | null;
  validUntil: number | null;
  bookingUrl: string | null;
  audienceSize: number | null;
  preparedAt: number | null;
  /** Pacientes atribuidas a la campaña. */
  patients: number;
  /**
   * Lo que esas pacientes facturaron, en centavos y por moneda. Separado y no
   * sumado: no hay cotización con la que juntarlas sin inventar un número.
   */
  revenue: Record<Currency, number>;
  normalizedSpendUsd: number | null;
  normalizedRevenueUsd: number | null;
  normalizationCoverage: {
    normalizedEntries: number;
    totalEntries: number;
  };
  deliveryMetrics: {
    recipients: number;
    delivered: number;
    opened: number;
    appointments: number;
  };
  createdAt: number;
  updatedAt: number;
}

export interface FinancialEntry {
  id: string;
  type: "income" | "expense";
  category: string;
  categoryId: string | null;
  /** Set only on income attributed to a patient; null for rent, ads, salaries. */
  patientId: string | null;
  /** La consulta que originó el cobro, cuando la hay. */
  appointmentId: string | null;
  /**
   * El tratamiento cobrado: lo escrito a mano en el movimiento y, si no hay
   * nada escrito, el de la consulta. Lo resuelve el API.
   */
  treatment: string | null;
  /**
   * En qué sede entró o salió. Lo propio y, si no hay, lo del turno; lo
   * resuelve el API. Nulo es legítimo: el alquiler y la pauta no son de
   * ninguna sede.
   */
  locationId: string | null;
  description: string;
  amount: number;
  /** En qué moneda entró. No se convierte: es lo que se cobró. */
  currency: Currency;
  /** Centavos USD usando el promedio compra/venta del día del movimiento. */
  normalizedAmountUsd: number | null;
  /** Día efectivo de la tasa usada; puede ser el hábil anterior. */
  exchangeRateDate: string | null;
  exchangeRateAverage: number | null;
  occurredAt: number;
  createdAt: number;
}

/** Ingreso acumulado por paciente, valuado a USD en la fecha de cada cobro. */
export interface PatientSpend {
  patientId: string;
  totalUsd: number | null;
  normalizedEntries: number;
  totalEntries: number;
}
export interface FinancialCategory {
  id: string;
  name: string;
  type: "income" | "expense";
  createdAt: number;
}
export interface SurveyResponse {
  id: string;
  patientId: string | null;
  /** La visita puntuada, cuando la encuesta salió de una. */
  appointmentId: string | null;
  score: number;
  rating: number | null;
  professionalId: string | null;
  locationId: string | null;
  contactConsent: boolean;
  anonymous: boolean;
  comment: string | null;
  createdAt: number;
  /** Resuelto desde el turno: el motivo de la consulta. */
  treatment: string | null;
  patientFirstName: string | null;
  patientLastName: string | null;
}

/** Las cinco familias de la cronología de la ficha. */
export type TimelineCategory =
  "clinical" | "agenda" | "documents" | "communication" | "finance";

export interface TimelineLink {
  kind:
    "encounter" | "appointment" | "document" | "photo" | "message" | "entry";
  id: string;
  appointmentId: string | null;
}

/**
 * Un evento de la cronología, ya normalizado por el servidor.
 *
 * `clinicalAt` es cuándo pasó y `recordedAt` cuándo se cargó: la ficha ordena
 * por el primero y aclara el segundo cuando no coinciden.
 */
export interface TimelineEvent {
  id: string;
  category: TimelineCategory;
  type: string;
  clinicalAt: number;
  recordedAt: number;
  title: string;
  summary: string | null;
  status: string | null;
  amended: boolean;
  amendmentNote: string | null;
  actor: string | null;
  link: TimelineLink;
  printable: boolean;
}

export interface TimelinePage {
  events: TimelineEvent[];
  /** Las familias que este perfil puede ver: el filtro no ofrece otras. */
  categories: TimelineCategory[];
  nextCursor: string | null;
}

export interface TimelineSummary {
  alerts: {
    allergies: string | null;
    contraindications: string | null;
    conditions: string | null;
  };
  counts: {
    encounters: number | null;
    appointments: number | null;
    upcoming: number | null;
    documents: number | null;
  };
  upcoming: {
    id: string;
    startsAt: number;
    professionalName: string | null;
    treatmentName: string | null;
  }[];
  pendingEncounters: { id: string; status: string; startedAt: number }[];
}

export type TaskType =
  "result_review" | "callback" | "follow_up" | "document" | "admin";
export type TaskStatus =
  "open" | "in_progress" | "blocked" | "done" | "cancelled";
export type TaskPriority = "low" | "normal" | "high" | "urgent";
export type TaskQueue = "clinical" | "reception" | "billing" | "admin";
export type TaskSource =
  "manual" | "appointment" | "encounter" | "consent" | "document";

/**
 * Un pendiente con dueño.
 *
 * `assigneeId` o `queue`: nunca los dos en null. El servidor lo garantiza, y
 * la bandeja lo asume para decidir a quién mostrárselo.
 */
export interface ClinicalTask {
  id: string;
  ownerId: string;
  type: TaskType;
  title: string;
  detail: string;
  patientId: string | null;
  sourceType: TaskSource;
  sourceId: string | null;
  dedupeKey: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
  queue: TaskQueue | null;
  priority: TaskPriority;
  status: TaskStatus;
  dueAt: number | null;
  resolution: string | null;
  resolvedAt: number | null;
  resolvedById: string | null;
  resolvedByName: string | null;
  autoResolved: boolean;
  createdById: string | null;
  createdByName: string | null;
  version: number;
  createdAt: number;
  updatedAt: number;
}

/** Lo que agrega la bandeja a cada tarea: nombre de la paciente y si venció. */
export interface TaskRow extends ClinicalTask {
  overdue: boolean;
  patientName: string | null;
}

export type TaskEventKind =
  | "created"
  | "assigned"
  | "status"
  | "comment"
  | "due"
  | "priority"
  | "resolved"
  | "reopened";

export interface ClinicalTaskEvent {
  id: string;
  taskId: string;
  kind: TaskEventKind;
  fromValue: string | null;
  toValue: string | null;
  comment: string | null;
  actorId: string | null;
  actorName: string | null;
  createdAt: number;
}

export interface TaskCounts {
  mine: number;
  team: number;
  unassigned: number;
  overdue: number;
}

export type TaskInbox = "mine" | "team" | "unassigned" | "overdue";

export interface TaskFilters {
  inbox: TaskInbox;
  status?: TaskStatus[];
  type?: TaskType[];
  queue?: TaskQueue[];
  assignee?: string;
  patientId?: string;
}

export type VisitState =
  | "expected"
  | "arrived"
  | "in_admission"
  | "waiting"
  | "called"
  | "in_consultation"
  | "finished"
  | "left";
export type VisitPriority = "normal" | "priority" | "urgent";
export type CheckinSource = "reception" | "link" | "kiosk";
export type ExitReason =
  | "completed"
  | "left_without_care"
  | "rescheduled"
  | "no_show"
  | "wrong_location";

/** Una visita en la cola del día, tal como la ve el tablero de recepción. */
export interface QueueEntry {
  id: string;
  ownerId: string;
  appointmentId: string | null;
  patientId: string;
  locationId: string | null;
  professionalId: string | null;
  resourceId: string | null;
  encounterId: string | null;
  clinicalDay: string;
  status: VisitState;
  displayAlias: string;
  priority: VisitPriority;
  priorityReason: string | null;
  checkinSource: CheckinSource | null;
  companions: number;
  accessibilityNeeds: string | null;
  pendingDocuments: boolean;
  pendingPayment: boolean;
  scheduledAt: number | null;
  arrivedAt: number | null;
  admissionStartedAt: number | null;
  admissionEndedAt: number | null;
  calledAt: number | null;
  consultationStartedAt: number | null;
  consultationEndedAt: number | null;
  departedAt: number | null;
  exitReason: ExitReason | null;
  recordedOffline: boolean;
  version: number;
  createdAt: number;
  updatedAt: number;
  patientName: string;
  professionalName: string | null;
  /** Minutos desde la llegada real; null mientras no haya llegado. */
  waitedMinutes: number | null;
}

export interface QueueBoard {
  entries: QueueEntry[];
  counters: {
    waiting: number;
    inConsultation: number;
    finished: number;
    overThreshold: number;
  };
  thresholdMinutes: number;
  /** Cuándo se armó esta foto: el tablero muestra cuán vieja es. */
  generatedAt: number;
}

/** La pantalla de la sala: alias y nada más. */
export interface QueueDisplay {
  calls: {
    alias: string;
    status: VisitState;
    room: string | null;
    calledAt: number | null;
  }[];
  generatedAt: number;
}

export interface QueueMetrics {
  visits: number;
  waitAverage: number | null;
  waitMedian: number | null;
  waitP75: number | null;
  waitP90: number | null;
  admissionAverage: number | null;
  delayAverage: number | null;
  abandonRate: number | null;
  digitalCheckinRate: number | null;
  /** Sobre qué proporción de visitas se calculó todo lo anterior. */
  coverage: number | null;
}

export interface QueueMetricsReport {
  overall: QueueMetrics;
  byProfessional: (QueueMetrics & { professionalName: string })[];
}

export interface QueueEvent {
  id: string;
  entryId: string;
  fromStatus: VisitState | null;
  toStatus: VisitState;
  reason: string | null;
  actorId: string | null;
  actorName: string | null;
  occurredAt: number;
  createdAt: number;
}

export type AllergyCategory =
  "medication" | "food" | "environment" | "biologic" | "other";
export type AllergySeverity = "mild" | "moderate" | "severe";
export type AllergyStatus =
  "active" | "inactive" | "refuted" | "entered_in_error";
export type MedicationStatus =
  "active" | "suspended" | "completed" | "unknown" | "entered_in_error";
export type ClinicalSource =
  "patient_reported" | "clinician" | "external_record";

export interface PatientAllergy {
  id: string;
  ownerId: string;
  patientId: string;
  encounterId: string | null;
  substance: string;
  category: AllergyCategory;
  reaction: string | null;
  severity: AllergySeverity;
  status: AllergyStatus;
  source: ClinicalSource;
  onsetDate: string | null;
  note: string | null;
  statusReason: string | null;
  recordedById: string;
  recordedByName: string;
  recordedAt: number;
  createdAt: number;
  updatedAt: number;
}

export interface PatientMedication {
  id: string;
  ownerId: string;
  patientId: string;
  encounterId: string | null;
  name: string;
  form: string | null;
  dose: string | null;
  route: string | null;
  frequency: string | null;
  indication: string | null;
  status: MedicationStatus;
  source: ClinicalSource;
  startedOn: string | null;
  endedOn: string | null;
  statusReason: string | null;
  note: string | null;
  recordedById: string;
  recordedByName: string;
  recordedAt: number;
  createdAt: number;
  updatedAt: number;
}

/**
 * Tres estados, no un booleano: `unknown` es que nadie preguntó y
 * `none_known` es que alguien preguntó y no hay. La ficha los dice distinto.
 */
export type AllergyState = "unknown" | "none_known" | "recorded";

export interface ClinicalProfileSummary {
  allergies: PatientAllergy[];
  medications: PatientMedication[];
  severeAllergies: PatientAllergy[];
  allergyState: AllergyState;
  noKnownAllergies: {
    at: number;
    byId: string | null;
    byName: string | null;
  } | null;
  /** Los campos de texto viejos, hasta que alguien los estructure. */
  legacy: { allergies: string | null; medications: string | null };
}

export interface ClinicalProfileEvent {
  id: string;
  patientId: string;
  subjectType: "allergy" | "medication";
  subjectId: string;
  action: string;
  fromStatus: string | null;
  toStatus: string | null;
  reason: string | null;
  changes: Record<string, string | null>;
  encounterId: string | null;
  actorId: string;
  actorName: string;
  createdAt: number;
}

export type ProblemStatus =
  "active" | "resolved" | "inactive" | "entered_in_error";
export type ProblemSeverity = "mild" | "moderate" | "severe";
export type ProblemCodeSystem = "local" | "icd10" | "snomed";
/** Por qué la lista está vacía: nadie preguntó, no refiere, no se evaluó. */
export type ProblemListState = "unknown" | "none_reported" | "not_assessed";

export interface PatientProblem {
  id: string;
  ownerId: string;
  patientId: string;
  encounterId: string | null;
  title: string;
  code: string | null;
  codeSystem: ProblemCodeSystem | null;
  status: ProblemStatus;
  severity: ProblemSeverity | null;
  onsetDate: string | null;
  resolvedDate: string | null;
  note: string | null;
  statusReason: string | null;
  recordedById: string;
  recordedByName: string;
  recordedAt: number;
  createdAt: number;
  updatedAt: number;
}

export interface EncounterDiagnosis {
  id: string;
  ownerId: string;
  encounterId: string;
  patientId: string;
  problemId: string | null;
  text: string;
  code: string | null;
  codeSystem: ProblemCodeSystem | null;
  primary: boolean;
  recordedById: string;
  recordedByName: string;
  createdAt: number;
}

export interface ProblemSummary {
  active: PatientProblem[];
  resolved: PatientProblem[];
  listState: ProblemListState | "recorded";
  listStateRecordedBy: {
    at: number | null;
    byId: string | null;
    byName: string | null;
  } | null;
  legacyConditions: string | null;
}

export interface ProblemEvent {
  id: string;
  patientId: string;
  problemId: string;
  action: string;
  fromStatus: string | null;
  toStatus: string | null;
  reason: string | null;
  changes: Record<string, string | null>;
  encounterId: string | null;
  actorId: string;
  actorName: string;
  createdAt: number;
}

export type VitalType =
  | "blood_pressure"
  | "heart_rate"
  | "temperature"
  | "oxygen_saturation"
  | "weight"
  | "height"
  | "bmi"
  | "perimeter";

export interface PatientVital {
  id: string;
  ownerId: string;
  patientId: string;
  encounterId: string | null;
  type: VitalType;
  /** Siempre en la unidad canónica del tipo. */
  value: number;
  secondaryValue: number | null;
  unit: string;
  /** Lo que se tecleó, tal cual, para poder releerlo. */
  enteredValue: number;
  enteredSecondaryValue: number | null;
  enteredUnit: string;
  method: string | null;
  measuredAt: number;
  measuredByName: string | null;
  outOfRangeConfirmed: boolean;
  note: string | null;
  recordedById: string;
  recordedByName: string;
  supersededAt: number | null;
  supersededById: string | null;
  supersededByMeasurementId: string | null;
  correctionReason: string | null;
  correctsMeasurementId: string | null;
  createdAt: number;
}

export interface VitalsSummary {
  latest: Record<VitalType, PatientVital | null>;
  /** Calculado a partir del peso y la altura; no se guarda. */
  derivedBmi: number | null;
}

export interface VitalSeries {
  type: VitalType;
  unit: string;
  timeZone: string;
  points: {
    id: string;
    measuredAt: number;
    /** El día clínico, en la zona de la clínica. */
    day: string;
    value: number;
    secondaryValue: number | null;
    outOfRangeConfirmed: boolean;
  }[];
}

export type OrderType = "lab" | "imaging" | "referral" | "other";
export type OrderStatus =
  "pending" | "received" | "reviewed" | "patient_notified" | "canceled";
export type ResultSource = "clinic" | "external" | "patient";

export interface ClinicalOrderResult {
  id: string;
  ownerId: string;
  patientId: string;
  orderId: string | null;
  summary: string;
  values: { name: string; value: string; unit?: string; flag?: string }[];
  uploadId: string | null;
  source: ResultSource;
  sourceName: string | null;
  performedAt: number | null;
  receivedAt: number;
  receivedById: string;
  receivedByName: string;
  createdAt: number;
}

export interface ClinicalOrder {
  id: string;
  ownerId: string;
  patientId: string;
  encounterId: string;
  type: OrderType;
  title: string;
  clinicalQuestion: string | null;
  status: OrderStatus;
  /** Nota del equipo: nunca viaja con la paciente. */
  internalNote: string | null;
  requestedById: string;
  requestedByName: string;
  requestedAt: number;
  reviewedById: string | null;
  reviewedByName: string | null;
  reviewedAt: number | null;
  patientNotifiedAt: number | null;
  patientNotifiedById: string | null;
  patientNotifiedVia: string | null;
  canceledAt: number | null;
  canceledById: string | null;
  cancelReason: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface ClinicalOrderWithResults extends ClinicalOrder {
  results: ClinicalOrderResult[];
}

/** Una orden de la bandeja, con el nombre de la paciente resuelto. */
export interface InboxOrder extends ClinicalOrder {
  patientName: string | null;
}

export interface ClinicalInstruction {
  id: string;
  ownerId: string;
  patientId: string;
  encounterId: string | null;
  text: string;
  patientVisible: boolean;
  authorId: string;
  authorName: string;
  createdAt: number;
}

/** Una ficha que el portal puede mostrarle a la cuenta que entró. */
export interface PortalLink {
  linkId: string;
  ownerId: string;
  patientId: string;
  firstName: string;
  lastName: string;
  clinic: string | null;
  confirmedAt: number | null;
  represented?: boolean;
}

export type FamilyRelationshipType =
  | "mother"
  | "father"
  | "guardian"
  | "caregiver"
  | "emergency_contact"
  | "authorized"
  | "financially_responsible";
export type RelationshipScope =
  | "portal"
  | "appointments"
  | "documents"
  | "clinical"
  | "communications"
  | "billing"
  | "consent";

export interface FamilyContact {
  id: string;
  linkedPatientId: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  documentId: string | null;
}

export interface PatientRelationship {
  id: string;
  patientId: string;
  contactId: string;
  type: FamilyRelationshipType;
  scopes: RelationshipScope[];
  status: "pending" | "verified" | "rejected" | "revoked";
  legalCapacity: string | null;
  evidenceUploadId: string | null;
  validFrom: number;
  validUntil: number | null;
  reviewAt: number | null;
  preferredForCommunication: boolean;
  preferredForBilling: boolean;
  version: number;
  verifiedAt: number | null;
  revokedAt: number | null;
  revokeReason: string | null;
}

export interface PortalAppointment {
  id: string;
  ownerId: string;
  patientId: string;
  startsAt: number;
  durationMin: number;
  status: string;
  reason: string | null;
  treatment: string | null;
  professional: string | null;
  location: string | null;
  clinic: string | null;
}

export interface PortalVisit {
  id: string;
  ownerId: string;
  patientId: string;
  appointmentId: string | null;
  status: string;
  arrivedAt: number | null;
  clinicalDay: string;
  alias: string | null;
  clinic: string | null;
  /** A qué hora tenía el turno, si había turno. */
  scheduledAt: number | null;
  /** Con quién se atiende. */
  professional: string | null;
  /** Cuántas esperan delante, sin contarla a ella. */
  ahead: number;
}

export interface PortalInstruction {
  id: string;
  ownerId: string;
  patientId: string;
  text: string;
  authorName: string;
  createdAt: number;
  clinic: string | null;
}

export interface PortalOrder {
  id: string;
  ownerId: string;
  patientId: string;
  type: string;
  title: string;
  status: string;
  requestedAt: number;
  clinic: string | null;
  /** Nulo hasta que un profesional lo revisó; ver `portal.reads.ts`. */
  resultSummary: string | null;
  resultAt: number | null;
}

export interface PortalMedication {
  id: string;
  ownerId: string;
  patientId: string;
  name: string;
  form: string | null;
  dose: string | null;
  route: string | null;
  frequency: string | null;
  indication: string | null;
  clinic: string | null;
}

export interface PortalDocument {
  id: string;
  ownerId: string;
  patientId: string;
  type: string;
  name: string;
  status: "issued" | "voided" | "replaced";
  issuedAt: number | null;
  clinic: string | null;
}

export interface PortalBalance {
  ownerId: string;
  patientId: string;
  clinic: string | null;
  currency: string;
  /** Unidad mínima de la moneda. Positivo es deuda; negativo, saldo a favor. */
  amount: number;
}

export type ClinicalDocumentType =
  | "prescription"
  | "order"
  | "certificate"
  | "attestation"
  | "instructions"
  | "report"
  | "referral";

export interface ClinicalDocumentTemplate {
  id: string;
  ownerId: string;
  familyId: string;
  version: number;
  type: ClinicalDocumentType;
  name: string;
  status: "draft" | "pending_approval" | "published" | "archived";
  blocks: Array<{
    id: string;
    kind:
      "heading" | "paragraph" | "list" | "signature" | "spacer" | "page_break";
    text: string;
    items?: string[];
    lang?: string;
  }>;
  approvedByName: string | null;
  approvedAt: number | null;
  createdById: string;
  createdByName: string;
  createdAt: number;
  updatedAt: number;
}

export interface ClinicalDocumentRecord {
  id: string;
  patientId: string;
  type: ClinicalDocumentType;
  templateId: string;
  templateVersion: number;
  status: "draft" | "issued" | "voided" | "replaced";
  snapshot: {
    template: { name: string };
    professional: { name: string };
  } | null;
  issuedAt: number | null;
  voidReason: string | null;
  replacedById: string | null;
}

export interface PortalOverview {
  confirmed: PortalLink[];
  pending: PortalLink[];
  upcoming: PortalAppointment[];
  past: PortalAppointment[];
  visits: PortalVisit[];
  instructions: PortalInstruction[];
  orders: PortalOrder[];
  medications: PortalMedication[];
  documents: PortalDocument[];
  balances: PortalBalance[];
}

/** Un insumo que se fue con un tratamiento. */
export interface TreatmentSupplyUse {
  itemId: string;
  name: string | null;
  unit: string | null;
  lotNumber: string;
  reference: string | null;
  /** Cuánto se usó. Siempre positivo: el signo vive en el libro de stock. */
  quantity: number;
}

/** Un tratamiento efectivamente realizado, en la historia de la paciente. */
export interface PerformedTreatment {
  id: string;
  ownerId: string;
  patientId: string;
  encounterId: string | null;
  appointmentId: string | null;
  treatmentId: string | null;
  /** Lo que dice la historia, aunque el catálogo se haya renombrado después. */
  treatmentName: string;
  professionalId: string | null;
  performedAt: number;
  renewalDueAt: number | null;
  renewalRemindedAt: number | null;
  notes: string | null;
  recordedById: string;
  recordedByName: string;
  createdAt: number;
  supplies: TreatmentSupplyUse[];
}
